import { GetObjectDetailsRouteFunction } from "@organisms/Table/TableViewControls/TableViewTab/TableViewTab.types"

import { PRICEBOOK_ITEM_TYPE_URL_PARAM_MAP } from "@pages/Pricebook/PricebookList/views/PricebookTableView/PricebookTableView.utils"

import { BILLS_ROUTES } from "@routes/bills"
import { CATALOG_ROUTES } from "@routes/catalogs"
import { CLIENTS_ROUTES } from "@routes/clients"
import { CREDIT_NOTES_ROUTES } from "@routes/creditNotes"
import { ESTIMATES_ROUTES } from "@routes/estimates"
import { INVOICES_ROUTES } from "@routes/invoices"
import { JOBS_ROUTES } from "@routes/jobs"
import { JOB_SERIES_ROUTES } from "@routes/jobs-series"
import { PRICEBOOK_ITEMS_ROUTES } from "@routes/pricebook-items"
import { PURCHASE_ORDER_ROUTES } from "@routes/purchase-orders"
import { VENDOR_ROUTES } from "@routes/vendor"

import replaceSlugs from "./replaceSlugs"

export type ObjectsKeysMap = {
    [key in ObjectIndexName]: GetObjectDetailsRouteFunction
}

interface GetObjectDetailsRouteArgs {
    objectName?: ObjectIndexName
    hasDraftEstimateEditPermission?: boolean
    hasDraftJobEditPermission?: boolean
    hasDraftInvoiceEditPermission?: boolean
    hasDraftPurchaseOrderEditPermission?: boolean
    hasDraftBillEditPermission?: boolean
    serviceCompanySlug: User["service_company"]["slug"]
}

export default function getObjectDetailsRoute(
    args: GetObjectDetailsRouteArgs,
): ObjectsKeysMap | GetObjectDetailsRouteFunction {
    const {
        objectName,
        hasDraftEstimateEditPermission,
        hasDraftJobEditPermission,
        hasDraftInvoiceEditPermission,
        hasDraftPurchaseOrderEditPermission,
        hasDraftBillEditPermission,
        serviceCompanySlug,
    } = args

    const getObjectDetailsRoute: ObjectsKeysMap = {
        Estimate: (obj) => {
            const estimate = obj as Estimate | EstimateIndex
            const pathname =
                "is_draft" in estimate && estimate.is_draft && hasDraftEstimateEditPermission
                    ? ESTIMATES_ROUTES.UPDATE
                    : ESTIMATES_ROUTES.DETAILS
            return replaceSlugs(pathname, { service_company_slug: serviceCompanySlug, id: estimate.id })
        },
        Job: (obj) => {
            const job = obj as Job | JobIndex
            const pathname =
                "is_draft" in job && job.is_draft && hasDraftJobEditPermission
                    ? JOBS_ROUTES.UPDATE
                    : JOBS_ROUTES.DETAILS

            return replaceSlugs(pathname, { service_company_slug: serviceCompanySlug, id: job.id })
        },
        Invoice: (obj) => {
            const invoice = obj as Invoice | InvoiceIndex
            const pathname =
                "is_draft" in invoice && invoice.is_draft && hasDraftInvoiceEditPermission
                    ? INVOICES_ROUTES.UPDATE
                    : INVOICES_ROUTES.DETAILS
            return replaceSlugs(pathname, { service_company_slug: serviceCompanySlug, id: invoice.id })
        },
        CreditNote: (obj) => {
            const creditNote = obj as unknown as CreditNote
            const pathname =
                "is_draft" in creditNote && creditNote.is_draft && hasDraftInvoiceEditPermission
                    ? CREDIT_NOTES_ROUTES.UPDATE
                    : CREDIT_NOTES_ROUTES.DETAILS
            return replaceSlugs(pathname, { service_company_slug: serviceCompanySlug, id: creditNote.id })
        },
        PurchaseOrder: (obj) => {
            const purchaseOrder = obj as PurchaseOrder | PurchaseOrderIndex
            const pathname =
                "is_draft" in purchaseOrder && purchaseOrder.is_draft && hasDraftPurchaseOrderEditPermission
                    ? PURCHASE_ORDER_ROUTES.UPDATE
                    : PURCHASE_ORDER_ROUTES.DETAILS
            return replaceSlugs(pathname, { service_company_slug: serviceCompanySlug, id: purchaseOrder.id })
        },
        Bill: (obj) => {
            const bill = obj as Bill | BillIndex
            const pathname =
                "is_draft" in bill && bill.is_draft && hasDraftBillEditPermission
                    ? BILLS_ROUTES.UPDATE
                    : BILLS_ROUTES.DETAILS
            return replaceSlugs(pathname, { service_company_slug: serviceCompanySlug, id: bill.id })
        },
        Vendor: (obj) => {
            const vendor = obj as Vendor | VendorIndex
            return replaceSlugs(VENDOR_ROUTES.DETAILS, { service_company_slug: serviceCompanySlug, id: vendor.id })
        },
        JobSeries: (obj) => {
            const series = obj as JobSeries | JobSeriesIndex
            return replaceSlugs(JOB_SERIES_ROUTES.DETAILS, {
                service_company_slug: serviceCompanySlug,
                id: series.id,
            })
        },
        Client: (obj) => {
            const client = obj as Client | ClientIndex
            return replaceSlugs(CLIENTS_ROUTES.DETAILS, { service_company_slug: serviceCompanySlug, id: client.id })
        },
        PriceBookItem: (obj) => {
            const pricebookItem = obj as PricebookItem | PricebookItemIndex
            const pathname = pricebookItem.confirmed ? PRICEBOOK_ITEMS_ROUTES.UPDATE : PRICEBOOK_ITEMS_ROUTES.REVIEW
            const query = `?type=${PRICEBOOK_ITEM_TYPE_URL_PARAM_MAP[pricebookItem.pricebook_item_type]}`
            return `${replaceSlugs(pathname, { service_company_slug: serviceCompanySlug, id: String(pricebookItem.id) })}${query}`
        },
        CatalogItem: (obj) => {
            return replaceSlugs(CATALOG_ROUTES.ITEM_DETAILS, { id: String(obj.id) })
        },
    }

    if (objectName) {
        return getObjectDetailsRoute[objectName] ?? (() => "")
    } else {
        return getObjectDetailsRoute
    }
}
